import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { backlogApi } from "../api/backlog";
import type { BacklogEntryFormData, BacklogVoteValue } from "../types";

export const KEYS = {
  all: ["mgmt-backlog"] as const,
  list: ["mgmt-backlog", "list"] as const,
  members: ["mgmt-backlog", "members"] as const,
  comments: (entryId: string) => ["mgmt-backlog", "comments", entryId] as const,
};

export function useBacklogEntries() {
  return useQuery({ queryKey: KEYS.list, queryFn: () => backlogApi.list() });
}

export function useBacklogMembers() {
  return useQuery({ queryKey: KEYS.members, queryFn: backlogApi.members });
}

export function useBacklogComments(entryId: string | null) {
  return useQuery({
    queryKey: KEYS.comments(entryId ?? ""),
    queryFn: () => backlogApi.comments(entryId!),
    enabled: !!entryId,
  });
}

function useInvalidatingMutation<TArgs>(fn: (args: TArgs) => Promise<unknown>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.all }),
  });
}

export function useCreateEntry() {
  return useInvalidatingMutation((data: BacklogEntryFormData) => backlogApi.create(data));
}

export function useUpdateEntry() {
  return useInvalidatingMutation(
    ({ id, data }: { id: string; data: Partial<BacklogEntryFormData> }) =>
      backlogApi.update(id, data),
  );
}

export function useDeleteEntry() {
  return useInvalidatingMutation((id: string) => backlogApi.remove(id));
}

export function useVote() {
  return useInvalidatingMutation(
    ({ id, value }: { id: string; value: BacklogVoteValue | null }) =>
      backlogApi.vote(id, value),
  );
}

export function useAssignEntry() {
  return useInvalidatingMutation(
    ({ id, owner, decision }: { id: string; owner?: string; decision?: string }) =>
      backlogApi.assign(id, { owner, decision }),
  );
}

export function useCompleteEntry() {
  return useInvalidatingMutation((id: string) => backlogApi.complete(id));
}

export function useCloseEntry() {
  return useInvalidatingMutation(
    ({ id, decision }: { id: string; decision: string }) => backlogApi.close(id, decision),
  );
}

export function useReopenEntry() {
  return useInvalidatingMutation((id: string) => backlogApi.reopen(id));
}

export function useAddComment() {
  return useInvalidatingMutation(
    ({ id, content }: { id: string; content: string }) => backlogApi.addComment(id, content),
  );
}
